import { createSelector, createSlice } from '@reduxjs/toolkit';
import enums from '../utils/enums';
import { Afr, Fact, getDimensionForFact, ValidationError, factValue, bulkFactValues, AfrVersion } from "./AfrSlice";
import { Entity } from "./EntitySlice";
import { AfrEvents, DiscrepancyEvent, getDiscrepancyEvents } from "./EventsSlice";
import { Concept } from "./ConceptSlice";
import { RootState } from './store';
import utilityHelper from '../utils/utilityHelper';
import { TaxonomyTypeEnum } from './TaxonomyVersionSlice';

interface EligibilityRule {
    accountCode: string;
    entityTypesEligible: Array<string>;
    entitiesEligible: Array<string>;
}

// To be downloaded
interface DependencyRule {
    accountCode: string;
    dependencies: Array<string>;
    test: (accountCode: Fact, dependencies: Array<Fact>) => boolean;
    message: (accountCode: Fact, dependencies: Array<Fact>) => string;
}

/* Define search criteria to delete erros*/
export interface DeletionCriteria {
    section: string,
    errorType: string,
}

interface ValidationState {
    eligibilityRules: any,
    dependencyRules: any,
    errorList: Array<ErrorSummaryAFRSection>,
    supplementalGridErrorList: Array<ErrorGrid>,
    displayErrors: boolean,
}

export interface ErrorGrid {
    field: string,
    type: string,
    message: string,
}

export interface ValidationContext {
    afr: Afr;
    afrEvents: AfrEvents | null;
    entity: Entity;
    pendingTableFacts: Array<factValue>;
    pendingReplaceTableFacts: bulkFactValues | null;
    dimensionSectionMap: any | null;
}
export interface FactSectionMap {
    balanceSheet: Array<Fact>;
    revenues: Array<Fact>;
    expenditures: Array<Fact>;
    longTermDebt: Array<Fact>;
    audit: Array<Fact>;
    otherAfrData: Array<Fact>;
    affiliatedEntities: Array<Fact>,
    courtInnovations: Array<Fact>,
    legalAidProgram: Array<Fact>,
    lawLibrary: Array<Fact>,
    juvenileAlternativeProgram: Array<Fact>,
    partA: Array<Fact>,
    partB: Array<Fact>,
    partC: Array<Fact>,
    partD: Array<Fact>,
    partE: Array<Fact>,
    governmental: Array<Fact>,
    proprietary: Array<Fact>,
    fiduciary: Array<Fact>,
    componentUnits: Array<Fact>,
    invalid: Array<Fact>
}

export interface Section {
    sectionTitle: string,
    sectionCode: string,
    sectionPage: string,
    sectionIsOpen: boolean,
}

export interface AFRError {
    type: string,
    description: string,
    severity:number,
    entityId: string,
    affiliateId: string | null,
    year: string,
    timeStamp: string,
    linkTo: string,
    entityType: string, /* 03( section code)  : Balance account,  
                           04_1: Revenues, 04_2: Impact Fees
                           05: Expenditures
                           08: Affiliated Entities*/
    entityCode: string,  /* ex. 512.000 Account if Balance sheet, 
                            301524 if it is an affiliated entity    */

}

export interface ErrorSummaryAFRSection  {
    section: Section,
    sectionErrorCount: number,
    errorList: Array<AFRError>
}

const checkForMissingFacts = function (addFacts: Fact[], currentAfr: Afr) {
    if (addFacts.length > 0 && currentAfr.currentversion && currentAfr.currentversion.missingFacts && currentAfr.currentversion.missingFacts.length > 0) {
        // We have missing facts - remove any that match the fact(s) being pushed
        var addMap: any = {};
        addFacts.map(function (addf: Fact) {
            var addSig = utilityHelper.getRowSignature(addf.concept.id, addf.dimensions);
            addMap[addSig] = addf
        });
        var newMissingFacts: Fact[] = [];

        currentAfr.currentversion.missingFacts.map(function (mf: Fact) {
            var sig = utilityHelper.getRowSignature(mf.concept.id, mf.dimensions);
            if (!addMap.hasOwnProperty(sig)) {
                // This fact has been NOT been added so retain as a missing fact
                newMissingFacts.push(mf);
            }
        })
        if (newMissingFacts.length < currentAfr.currentversion.missingFacts.length) {
            currentAfr.currentversion.missingFacts = newMissingFacts;
        }
    }

}
var defaultAddUnassignedFact = function (f: Fact, av: AfrVersion, sMap: FactSectionMap, dmap: any) {
    if (f.concept.id.indexOf("LongTermDebt") >= 0) {
        sMap.longTermDebt.push(f);
    }
    else if (getDimensionForFact(f, "ReportingUnitAxis")) {
        sMap.affiliatedEntities.push(f);
    }
    else if (getDimensionForFact(f, "AfrNoDataAxis")) {
        var member = getDimensionForFact(f, "AfrNoDataAxis");
        if (member) {
            switch (member.id) {
                case "AFRND_ExpendituresMember":
                    sMap.expenditures.push(f);
                    break;
                case "AFRND_RevenuesMember":
                    sMap.revenues.push(f);
                    break;
                case "AFRND_BalanceSheetMember":
                    sMap.balanceSheet.push(f);
                    break;
                case "AFRND_GovernmentalMember":
                    sMap.governmental.push(f);
                    break;
                case "AFRND_ProprietaryMember":
                    sMap.proprietary.push(f);
                    break;
                case "AFRND_FiduciaryMember":
                    sMap.fiduciary.push(f);
                    break;
                case "AFRND_DiscreteComponentUnitsMember":
                    sMap.componentUnits.push(f);
                    break;
                case "AFRND_ConstructionProjectsMember":
                    sMap.otherAfrData.push(f);
                    break;
                case "AFRND_BondsMember":
                    sMap.otherAfrData.push(f);
                    break;
                case "AFRND_DRAuditMember":
                case "ADRND_DRImpactFeeExceptionMember":
                case "ADRND_DRImpactFeeWaiverMember":
                case "ADRND_DRImpactFeeAffidavitMember":
                case "ADRND_DRBudgetVarianceReportMember":
                case "ADRND_DRCR29SignedLetterMember":
                case "ADRND_DRCR29CPALetterMember":
                    sMap.otherAfrData.push(f);
                    break;

                default:
                    sMap.otherAfrData.push(f);
                    break;
            }
        }
    }
    else {
        sMap.otherAfrData.push(f);
    }
}
export const addUnassignedUASFact = function(afr: Afr, sMap: FactSectionMap, f: Fact, accountList : Fact[]) {
    if (afr.year >= 2024) {
        var fundGroup: Concept | any = getDimensionForFact(f, "FundGroupsAxis");
        if (fundGroup) {
            var identifier = fundGroup.name.split(" ")[0];
            switch (identifier) {
                case "001":
                case "050":
                case "100":
                case "200":
                case "300":
                    sMap.governmental.push(f);
                    break;
                case "600":
                case "650":
                case "700":
                case "750":
                    sMap.fiduciary.push(f);
                    break;
                case "400":
                case "500":
                    sMap.proprietary.push(f);
                    break;
                case "000":
                    sMap.componentUnits.push(f);
                    break;
            }
        } else {
            sMap.invalid.push(f);
        }
    } else {
        accountList.push(f);
    }
}
const addUnassignedFacts = function (afr: Afr, sMap: FactSectionMap, uFacts: Fact[], dmap: any) {
    var av: AfrVersion = afr.currentversion;
    if (!av) {
        return;
    }
    for (var f of uFacts) {
        if (getDimensionForFact(f, "ReportingUnitAxis")) {
            sMap.affiliatedEntities.push(f);
            continue;
        }
        if (f.concept.id.indexOf("Audit") >= 0) {
            sMap.audit.push(f);
            continue;
        }   
        if (f.concept.id.indexOf("BalanceSheetTitle") >= 0) {
            sMap.balanceSheet.push(f);
            continue;
        }
        var firstDigit = (f.accountCode && f.accountCode.length > 0) ? f.accountCode.substring(0, 1) : "I";
        switch (firstDigit) {
            case "1":
            case "2":
                addUnassignedUASFact(afr, sMap, f, sMap.balanceSheet);
                break;

            case "3":
            case "4":
                if (getDimensionForFact(f, "ReportingUnitAxis")) {
                    sMap.affiliatedEntities.push(f);
                } else {
                    addUnassignedUASFact(afr, sMap, f, sMap.revenues);
                }
                break;
            case "5":
            case "6":
            case "7":
                if (getDimensionForFact(f, "ReportingUnitAxis")) {
                    sMap.affiliatedEntities.push(f);
                } else {
                    addUnassignedUASFact(afr, sMap, f, sMap.expenditures);
                }
                break;
            case "I":
                sMap.invalid.push(f);
                break;

            case "C":
                if (f.accountCode.indexOf("CR_939") === 0) {
                    const sect: string = utilityHelper.getSectionForDimension(f, dmap);
                    switch (sect) {
                        case "CR_939_CourtInnovations":
                            sMap.courtInnovations.push(f);
                            break;
                        case "CR_939_LegalAidProgram":
                            sMap.legalAidProgram.push(f);
                            break;
                        case "CR_939_LawLibrary":
                            sMap.lawLibrary.push(f);
                            break;
                        case "CR_939_JuvenileAlternativeProgram":
                            sMap.juvenileAlternativeProgram.push(f);
                            break;
                        default:
                            sMap.invalid.push(f)
                            break;
                    }
                } else if (f.accountCode.indexOf("CR_29") === 0) {
                    const tname: string = utilityHelper.getTableNameForFact(av, f)
                    switch (tname) {
                        case "cr29-court-reporting-local-requirements-collections":
                            sMap.partA.push(f);
                            break;
                        case "cr29-court-reporting-non-recurring-expenditures":
                            sMap.partB.push(f);
                            break;
                        case "cr29-court-reporting-facility-cost":
                            sMap.partC.push(f);
                            break;
                        case "cr29-court-reporting-communications-systems-and-services":
                            sMap.partC.push(f);
                            break;
                        case "cr29-court-reporting-existing-systems":
                            sMap.partC.push(f);
                            break;
                        case "cr29-court-reporting-local-requirements":
                            sMap.partD.push(f);
                            break;
                        case "cr29-court-reporting-other-operating-court-related-expenditures":
                            sMap.partE.push(f);
                            break;
                        default:
                            sMap.invalid.push(f)
                            break;
                    }

                } else {
                    defaultAddUnassignedFact(f, av, sMap, dmap);
                }
                break;

            default:
                defaultAddUnassignedFact(f, av, sMap, dmap);
                break;
        }
    }
}

function processForValidationErrors(f: Fact, factSignatureMap: any) {
    const sig = utilityHelper.getRowSignature(f.concept.id, f.dimensions);
    if (sig.length > 5) {
        factSignatureMap[sig] = f;
        var alternateSigs: string | null = null;
        switch (sig.charAt(0)) {
            case 'B':
                if (sig.substr(0, 4) === "BS_1") {
                    alternateSigs = "BS_1xxxxx_AssetsOtherDebitsDeferredOutflow";
                } else {
                    alternateSigs = "BS_2xxxxx_LiabilitiesOtherCreditsDeferredInflow";
                }
                break;

            case 'R':
                if (sig.substring(0, 5) === "Rev_3") {
                    alternateSigs = "Rev_3xxxxx_Revenue";
                }
                break;

            case 'E':
                switch (sig.charAt(4)) {
                    case '5':
                        alternateSigs = "Exp_5xxxx_7xxxx_Expenditures|Exp_5xxxx_Expenditures";
                        break;

                    case '6':
                        alternateSigs = "Exp_5xxxx_7xxxx_Expenditures|Exp_6xxxx_CourtRelatedExpenditures";
                        break;

                    case '7':
                        alternateSigs = "Exp_5xxxx_7xxxx_Expenditures|Exp_7xxxx_CourtRelatedExpendituresContinued";
                        break;
                }
                break;
        }
        if (alternateSigs) {
            alternateSigs.split("|").map(s => factSignatureMap[s] = f);
        }

    }
    return f.validationerrors && f.validationerrors.length > 0; 
}
function addNoDataPlaceholder(vc: ValidationContext, section: string, factSignatureMap: any) {
    if (utilityHelper.isNoData(vc.afr, section, vc.entity)) {
        var conceptId = "";
        var accCode = "";
        var dimId : string | null = null;
        switch (section) {
            case enums.UISection.expenditures:
                conceptId = "Exp_5xxxx_7xxxx_Expenditures";
                accCode = "5xxxx-7xxxx";
                dimId = "AFRND_ExpendituresMember";
                break;

            case enums.UISection.revenues:
                conceptId = "Rev_3xxxxx_Revenue";
                accCode = "3xxxxx";
                dimId = "AFRND_RevenuesMember";
                break;

            case enums.UISection.otherAfrDataProjects:
                conceptId = "SDSupp_ConstructionProjectExpense";
                accCode = "ConstructionProjectsExpense";
                dimId = "AFRND_ConstructionProjectsMember";
                break;

            case enums.UISection.otherAfrDataBonds:
                conceptId = "SDSupp_BondTotalOutstandingAmount";
                accCode = "BondTotalOutstandingAmount";
                dimId = "AFRND_BondsMember";
                break;

            default:
                break;
        }
        var ndRootFact: Fact = {
            value: 0,
            textValue: null,
            dimensions: [],
            concept: { id: conceptId },
            validationerrors: [],
            accountCode: accCode
        }
        factSignatureMap[conceptId] = ndRootFact;
        if (dimId) {
            conceptId = "AFRND_NoData";
            accCode = "ND_" + dimId;
            var ndFlagFact: Fact = {
                value: 0,
                textValue: null,
                dimensions: [],
                concept: { id: conceptId },
                validationerrors: [],
                accountCode: accCode
            }
            var ndKey = "AFRND_NoData|AfrNoDataAxis#" + dimId;
            factSignatureMap[ndKey] = ndFlagFact;
        }
    }
}
function checkMissingAgainstPresent(vc: ValidationContext, mf: Fact, factSignatureMap: any): boolean {
    var sig: string = utilityHelper.getRowSignature(mf.concept.id, mf.dimensions);
    return factSignatureMap.hasOwnProperty(sig);
}
export function generateFactErrorMap(vc : ValidationContext) {
    var sMap: FactSectionMap = {
        balanceSheet: [],
        revenues: [],
        expenditures: [],
        longTermDebt: [],
        audit: [],
        otherAfrData: [],
        affiliatedEntities: [],
        courtInnovations: [],
        legalAidProgram: [],
        lawLibrary: [],
        juvenileAlternativeProgram: [],
        partA: [],
        partB: [],
        partC: [],
        partD: [],
        partE: [],
        governmental: [],
        proprietary: [],
        fiduciary: [],
        componentUnits: [],
        invalid: []
    };
    const currentAfr: Afr | null = vc.afr;
    if (currentAfr && currentAfr.currentversion) {
        const factSignatureMap: any = {};
        if (currentAfr.currentversion.tables) {
            currentAfr.currentversion.tables.map(t =>
                t.rows.map(r =>
                    addUnassignedFacts(currentAfr, sMap, r.facts.filter(f => processForValidationErrors(f, factSignatureMap)), vc.dimensionSectionMap)
                )
            )
        }
        if (currentAfr.currentversion.unassignedFacts) {
            addUnassignedFacts(currentAfr, sMap, currentAfr.currentversion.unassignedFacts, vc.dimensionSectionMap);
        }
        // Before we process the missing facts, we should convert any "no data" settings into equivalent masking facts
        addNoDataPlaceholder(vc, enums.UISection.revenues, factSignatureMap);
        addNoDataPlaceholder(vc, enums.UISection.expenditures, factSignatureMap);
        addNoDataPlaceholder(vc, enums.UISection.otherAfrDataProjects, factSignatureMap);
        addNoDataPlaceholder(vc, enums.UISection.otherAfrDataBonds, factSignatureMap);
        if (currentAfr.currentversion.missingFacts) {
            var remainingMissingFacts = currentAfr.currentversion.missingFacts.filter(
                mf => !checkMissingAgainstPresent(vc, mf, factSignatureMap)
            );
            addUnassignedFacts(currentAfr, sMap, remainingMissingFacts, vc.dimensionSectionMap);
        }
    }
    return sMap;
}

function addUnassignedFactErrors(section: Section, year: number, e: Entity, facts: Array<Fact>, errorList: Array<ErrorSummaryAFRSection>) {
    const timestamp: string = new Date(Date.now()).toISOString();
    if (facts.length > 0) {
        const esum: ErrorSummaryAFRSection = {
            section: section,
            sectionErrorCount: 0,
            errorList: []
        }

        for (var f of facts) {
            if (f.validationerrors && f.validationerrors.length > 0) {
                for (var verror of f.validationerrors) {
                    if (verror.category === "IMPORT_ERROR") {
                        // This will be handled by an explicit processing of afr validation errors.
                        continue;
                    }
                    var affiliate: string | null = null;
                    var mc = getDimensionForFact(f, "ReportingUnitAxis");
                    if (mc) {
                        affiliate = mc.id;
                    }
                    var fdesc = verror.message;
                    if( f.accountCode && f.accountCode.length > 0 ) {
                        fdesc = f.accountCode + ": " + verror.message;
                    }
                    var sev : number = enums.severity.error;
                    if( verror.severity ) {
                        switch(verror.severity) {
                            case "WARNING":
                                sev = enums.severity.warning;
                                break;

                            case "INFO":
                                sev = enums.severity.info;
                                break;
                        }
                    }
                    const messageError: AFRError = {
                        type: "Unassigned fact",
                        severity: sev,
                        description: fdesc,
                        linkTo: "",
                        entityId: e.code,
                        affiliateId: affiliate,
                        year: year + "",
                        timeStamp: timestamp,
                        entityType: e.typecode,
                        entityCode: e.code,
                    }
                    esum.errorList.push(messageError);
                }
            }
        }
        if (esum.errorList.length > 0) {
            esum.sectionErrorCount = esum.errorList.length;
            errorList.push(esum);
        }
    }
}

function addImportErrors(section: Section, year: number, e: Entity, validationErrors : Array<ValidationError>, errorList: Array<ErrorSummaryAFRSection>) {
    const timestamp: string = new Date(Date.now()).toISOString();
    if (validationErrors.length > 0) {
        const esum: ErrorSummaryAFRSection = {
            section: section,
            sectionErrorCount: 0,
            errorList: []
        }

        for (var verror of validationErrors) {
            var sev: number = enums.severity.error;
            if (verror.severity) {
                switch (verror.severity) {
                    case "WARNING":
                        sev = enums.severity.warning;
                        break;

                    case "INFO":
                        sev = enums.severity.info;
                        break;
                }
            }
            const messageError: AFRError = {
                type: "Import error",
                description: verror.message,
                severity: sev,
                linkTo: "",
                entityId: e.code,
                affiliateId: null,
                year: year + "",
                timeStamp: timestamp,
                entityType: e.typecode,
                entityCode: e.code,
            }
            esum.errorList.push(messageError);
        }
        if (esum.errorList.length > 0) {
            esum.sectionErrorCount = esum.errorList.length;
            errorList.push(esum);
        }
    }
}

export function addBusinessRulesErrors(afr: Afr, section: Section, errorList: Array<AFRError>): ErrorSummaryAFRSection    {

    const errorSection: ErrorSummaryAFRSection = {
        section: section,
        sectionErrorCount: 0,
        errorList: []
    }

    if (errorList.length > 0) {
        for (var err of errorList) {
            errorSection.errorList.push(err);
        }

        errorSection.sectionErrorCount = errorSection.errorList.length;

    }
    return errorSection
}


export const getValidationsForAfr = function (vc : ValidationContext): Array<ErrorSummaryAFRSection> {
    const errorList: Array<ErrorSummaryAFRSection> = [];
    const fsm: FactSectionMap = generateFactErrorMap(vc);
    if (!vc.afr || !vc.afr.currentversion) {
        return errorList;
    }
    var sumPath = "/AfrSummaryA";
    var ttPrefix = "/";
    switch (vc.afr.taxonomyType) {
        case TaxonomyTypeEnum.COURT_REPORTING_29:
            ttPrefix += "CR29";
            sumPath = "/CR29AdminA";
            break;
        case TaxonomyTypeEnum.COURT_REPORTING_939:
            ttPrefix += "CR939";
            sumPath = "/CourtReporting939AdminA";
            break;
    }
    const de: DiscrepancyEvent | null = {
        afrId: vc.afr.afrId,
        entityName: vc.entity ? vc.entity.name : "",
        firstDiscrepancy: null,
        secondDiscrepancy: null,
        thirdDiscrepancy: null
    }; //getDiscrepancyEvents(vc.afr, vc.entity, vc.afrEvents);
    if (de !== null && de && de.firstDiscrepancy) {
        const dSection: Section = {
            sectionCode: enums.UISection.summary,
            sectionPage: sumPath,
            sectionTitle: "Summary Section",
            sectionIsOpen: false,
        };
        const mev = de.thirdDiscrepancy ? de.thirdDiscrepancy : (de.secondDiscrepancy ? de.secondDiscrepancy : de.firstDiscrepancy);
        const dSectionErrors: ErrorSummaryAFRSection = {
            section: {
                sectionCode: enums.UISection.summary,
                sectionPage: sumPath,
                sectionTitle: "Summary Section",
                sectionIsOpen: false,
            },
            sectionErrorCount: 1,
            errorList: [
                {
                    type: "Discrepancy Error",
                    severity: enums.severity.error,
                    description: "Description for discrepancy error in summary section",
                    linkTo: "/ErrorsA",
                    entityId: vc.entity.code,
                    affiliateId: null,
                    year: vc.afr.year + "",
                    timeStamp: mev.timestamp,
                    entityType: vc.entity.typecode,
                    entityCode: vc.entity.code
                }
            ]

        };
        errorList.push(dSectionErrors)
    }
    const errSection: Section = {
        sectionCode: enums.UISection.errors,
        sectionPage: ttPrefix + "Errors",
        sectionTitle: "Errors",
        sectionIsOpen: false,
    };
    const importErrSection: Section = {
        sectionCode: enums.UISection.importErrors,
        sectionPage: ttPrefix + "Errors",
        sectionTitle: "Import Errors",
        sectionIsOpen: false,
    };
    const bsSection: Section = {
        sectionCode: enums.UISection.balanceSheet,
        sectionPage: "/BalanceSheetA",
        sectionTitle: "Balance Sheet",
        sectionIsOpen: false,
    };
    const revSection: Section = {
        sectionCode: enums.UISection.revenues,
        sectionPage: "/RevenuesA",
        sectionTitle: "Revenues",
        sectionIsOpen: false,
    };
    const expSection: Section = {
        sectionCode: enums.UISection.expenditures,
        sectionPage: "/ExpendituresA",
        sectionTitle: "Expenditures",
        sectionIsOpen: false,
    };
    const ltdSection: Section = {
        sectionCode: enums.UISection.longTermDebt,
        sectionPage: "/LongTermDebtA",
        sectionTitle: "Long Term Debt",
        sectionIsOpen: false,
    };
    const audSection: Section = {
        sectionCode: enums.UISection.audit,
        sectionPage: "/AuditA",
        sectionTitle: "Audit",
        sectionIsOpen: false,
    };
    const otherSection: Section = {
        sectionCode: enums.UISection.otherAfrData,
        sectionPage: "/AfrDataA",
        sectionTitle: "Supplemental Information",
        sectionIsOpen: false,
    };
    const affiliatesSection: Section = {
        sectionCode: enums.UISection.affiliatedEntities,
        sectionPage: "/AffiliatedEntitiesA",
        sectionTitle: "Affiliated Entities",
        sectionIsOpen: false,
    };
    const courtInnovationsSection: Section = {
        sectionCode: enums.UISection_CR939.courtInnovations,
        sectionPage: "/CourtReporting939CIA",
        sectionTitle: "Court Innovations",
        sectionIsOpen: false,
    };
    const legalAidProgramSection: Section = {
        sectionCode: enums.UISection_CR939.legalAidProgram,
        sectionPage: "/CourtReporting939LAPA",
        sectionTitle: "Legal Aid Program",
        sectionIsOpen: false,
    };
    const lawLibrarySection: Section = {
        sectionCode: enums.UISection_CR939.lawLibrary,
        sectionPage: "/CourtReporting939LLA",
        sectionTitle: "Law Library",
        sectionIsOpen: false,
    };
    const juvenileAlternativeProgramSection: Section = {
        sectionCode: enums.UISection_CR939.juvenileAlternativeProgram,
        sectionPage: "/CourtReporting939JAPA",
        sectionTitle: "Juvenile Alternative Program",
        sectionIsOpen: false,
    };
    const partASection: Section = {
        sectionCode: enums.UISection_CR29.partA,
        sectionPage: "/CR29DataAA",
        sectionTitle: "Part A",
        sectionIsOpen: false,
    };

    const partBSection: Section = {
        sectionCode: enums.UISection_CR29.CR29PartB,
        sectionPage: "/CR29DataBA",
        sectionTitle: "Part B",
        sectionIsOpen: false,
    };

    const partCSection: Section = {
        sectionCode: enums.UISection_CR29.CR29PartC,
        sectionPage: "/CR29DataCA",
        sectionTitle: "Part C",
        sectionIsOpen: false,
    };

    const partDSection: Section = {
        sectionCode: enums.UISection_CR29.CR29PartD,
        sectionPage: "/CR29DataDA",
        sectionTitle: "Part D",
        sectionIsOpen: false,
    };

    const partESection: Section = {
        sectionCode: enums.UISection_CR29.CR29PartE,
        sectionPage: "/CR29DataEA",
        sectionTitle: "Part E",
        sectionIsOpen: false,
    };
    const govSection: Section = {
        sectionCode: enums.UISection_FundGroup.governmentalFunds,
        sectionPage: "/AccountsByGovernmentalA",
        sectionTitle: "Governmental Funds",
        sectionIsOpen: false,
    };
    const proSection: Section = {
        sectionCode: enums.UISection_FundGroup.proprietaryFunds,
        sectionPage: "/AccountsByProprietaryA",
        sectionTitle: "Proprietary Funds",
        sectionIsOpen: false,
    };

    const fidSection: Section = {
        sectionCode: enums.UISection_FundGroup.fiduciaryFunds,
        sectionPage: "/AccountsByFiduciaryA",
        sectionTitle: "Fiduciary Funds",
        sectionIsOpen: false,
    };

    const compSection: Section = {
        sectionCode: enums.UISection_FundGroup.componentUnitsFunds,
        sectionPage: "/AccountsByComponentUnitsA",
        sectionTitle: "Discrete Component Units Funds",
        sectionIsOpen: false,
    };

    addImportErrors(importErrSection, vc.afr.year, vc.entity, vc.afr.currentversion.validationerrors, errorList);
    switch (vc.afr.taxonomyType) {
        case "COURT_REPORTING_939":
            addUnassignedFactErrors(courtInnovationsSection, vc.afr.year, vc.entity, fsm.courtInnovations, errorList);
            addUnassignedFactErrors(legalAidProgramSection, vc.afr.year, vc.entity, fsm.legalAidProgram, errorList);
            addUnassignedFactErrors(lawLibrarySection, vc.afr.year, vc.entity, fsm.lawLibrary, errorList);
            addUnassignedFactErrors(juvenileAlternativeProgramSection, vc.afr.year, vc.entity, fsm.juvenileAlternativeProgram, errorList);
            break;

        case "COURT_REPORTING_29":
            addUnassignedFactErrors(partASection, vc.afr.year, vc.entity, fsm.partA, errorList);
            addUnassignedFactErrors(partBSection, vc.afr.year, vc.entity, fsm.partB, errorList);
            addUnassignedFactErrors(partCSection, vc.afr.year, vc.entity, fsm.partC, errorList);
            addUnassignedFactErrors(partDSection, vc.afr.year, vc.entity, fsm.partD, errorList);
            addUnassignedFactErrors(partESection, vc.afr.year, vc.entity, fsm.partE, errorList);
            break;

        default:
            if (vc.afr.year < 2024) {
                addUnassignedFactErrors(bsSection, vc.afr.year, vc.entity, fsm.balanceSheet, errorList);
                addUnassignedFactErrors(revSection, vc.afr.year, vc.entity, fsm.revenues, errorList);
                addUnassignedFactErrors(expSection, vc.afr.year, vc.entity, fsm.expenditures, errorList);
            } else {
                addUnassignedFactErrors(govSection, vc.afr.year, vc.entity, fsm.governmental, errorList);
                addUnassignedFactErrors(proSection, vc.afr.year, vc.entity, fsm.proprietary, errorList);
                addUnassignedFactErrors(fidSection, vc.afr.year, vc.entity, fsm.fiduciary, errorList);
                addUnassignedFactErrors(compSection, vc.afr.year, vc.entity, fsm.componentUnits, errorList);
            }
            addUnassignedFactErrors(ltdSection, vc.afr.year, vc.entity, fsm.longTermDebt, errorList);
            addUnassignedFactErrors(audSection, vc.afr.year, vc.entity, fsm.audit, errorList);
            addUnassignedFactErrors(otherSection, vc.afr.year, vc.entity, fsm.otherAfrData, errorList);
            addUnassignedFactErrors(affiliatesSection, vc.afr.year, vc.entity, fsm.affiliatedEntities, errorList);
            break;
    }
    addUnassignedFactErrors(errSection, vc.afr.year, vc.entity, fsm.invalid, errorList);
    return errorList;
}

export const getTotalErrorCountBySection = function (errorList: Array<ErrorSummaryAFRSection>, section: string) {
    if (!(errorList)) { return 0 };
    var errors = errorList.find(x => x.section.sectionCode === section);

    if ((typeof errors === "undefined")) {
        return 0;
    }

    return errors.sectionErrorCount;

};


const initialState: ValidationState = {
    eligibilityRules: new Map<string,EligibilityRule>(),
    dependencyRules: new Map<string, DependencyRule>(),
    errorList: [],  /* replace this by "null" */
    supplementalGridErrorList: [],
    displayErrors: false,
}

export const validationSlice = createSlice({
    name: 'Validation',
    initialState,
    reducers: {
        resetValidationState: (state: any, action: any) => {
            return initialState
        },
        validateAfr: (state: any, action: any) => {
            state.errorList = getValidationsForAfr(action.payload);
        },
        updateErrors: (state: any, action: any) => {
            var errors: Array<ErrorSummaryAFRSection> = action.payload;
            state.errorList = errors;
        },
        setDisplayErrors: (state: any, action: any) => {
            state.displayErrors = action.payload;
        },
        updateSupplementalGridErrors: (state: any, action: any) => {
            var errors: Array<ErrorGrid> = action.payload;
            var peList: ErrorGrid[] = [];
            state.supplementalGridErrorList.map((e: ErrorGrid) => peList.push({ ...e }));
            var existingError = state.supplementalGridErrorList.find((ferr: ErrorGrid) => ferr.message.indexOf("editing") > 0)
            if (existingError === undefined) {
                state.supplementalGridErrorList = errors;
            } else {
                var newErrors: ErrorGrid[] = [];
                var existingErrorUsed = false;
                errors.map((error: ErrorGrid) => {
                    var field: string = error.field;

                    if (existingError.field === error.field) {
                        newErrors.push({ ...existingError });
                        existingErrorUsed = true;
                    } else {
                        newErrors.push({ ...error });
                    }
                });
                if (!existingErrorUsed) {
                    newErrors.push({ ...existingError });
                }
                state.supplementalGridErrorList = newErrors;
            }
        },
        removeSingleSupplementalGridError: (state: any, action: any) => {
            var error: ErrorGrid = action.payload;
            var newErrors: ErrorGrid[] = [];
            var field: string = error.field;
            state.supplementalGridErrorList.map((eg: ErrorGrid) => {
                if (error.field.length > 0) {
                    if (eg.field !== error.field) {
                        newErrors.push({ ...eg });
                    }
                } else if (error.message.length > 0) {
                    if (eg.message.indexOf(error.message) < 0) {
                        newErrors.push({ ...eg });
                    }
                }

            });
            state.supplementalGridErrorList = newErrors;
        },
        updateSingleSupplementalGridError: (state: any, action: any) => {
            var error: ErrorGrid = action.payload;
            var bMatched: boolean = false;
            var newErrors: ErrorGrid[] = [];
            var field: string = error.field;
            state.supplementalGridErrorList.map((eg : ErrorGrid) => {
                if (eg.field === error.field) {
                    newErrors.push({ ...error });
                    bMatched = true;
                } else {
                    newErrors.push({ ...eg });
                }

            });
            if (!bMatched) {
                newErrors.push(error);
            }
            state.supplementalGridErrorList = newErrors;
        },

    },
});

export const getErrorList = (state: RootState) => state.validation ? state.validation.errorList : [];
export const getSupplementalGridErrorList = (state: RootState) => state.validation ? state.validation.supplementalGridErrorList : [];
export const getDisplayErrors = (state: RootState) => state.validation ? state.validation.displayErrors : false;

export const getTotalErrorCount = createSelector([getErrorList], (errorList: Array<ErrorSummaryAFRSection>) => {
    var sum = 0;
    errorList.map((i, k) => {
        sum = sum + i.sectionErrorCount;
    });
    return sum;
});

export const { resetValidationState, validateAfr, updateErrors, setDisplayErrors, updateSupplementalGridErrors, updateSingleSupplementalGridError, removeSingleSupplementalGridError } = validationSlice.actions;


export default validationSlice.reducer;